import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './BlogPostPage.css';

const BlogPostPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // If no slug, redirect to blog listing
        if (!slug) {
            navigate('/blog', { replace: true });
            return;
        }

        const fetchPost = async () => {
            setLoading(true);
            setError(null);

            try {
                // Resolve slug to canonical blob path; fall back to slug-as-path if API is unavailable.
                let blobPath = `blogs/${slug}/data.json`;
                try {
                    const resolveResponse = await fetch(`/api/resolve-page?type=blog&slug=${encodeURIComponent(slug)}`);
                    if (resolveResponse.ok) {
                        const resolved = await resolveResponse.json();
                        if (resolved && resolved.blobPath) {
                            blobPath = resolved.blobPath;
                        }
                    }
                } catch (resolveErr) {
                    // resolve-page unavailable; continue with direct fallback path
                }

                const dataResponse = await fetch(`https://physioactivityweb.blob.core.windows.net/${blobPath}`);
                if (!dataResponse.ok) throw new Error('Post not found');
                const data = await dataResponse.json();
                setPost(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug, navigate]);

    useEffect(() => {
        if (post) {
            document.title = `${post.title} | PhysioActivity Blog`;
        }
    }, [post]);

    // Initialise saved-state from localStorage once we know the slug
    useEffect(() => {
        if (!slug) return;
        try {
            const flag = window.localStorage.getItem(`pa-blog-saved-${slug}`);
            setSaved(flag === '1');
        } catch (e) {
            // localStorage may be unavailable (private mode etc.) — ignore
        }
    }, [slug]);

    // Inject Open Graph / Twitter Card meta tags for social sharing.
    // This is best-effort: LinkedIn and some modern crawlers run JS; older
    // crawlers won't see these. Proper SSR for OG is out of scope.
    useEffect(() => {
        if (!post) return;

        const description =
            post.excerpt ||
            (post.content
                ? post.content.replace(/<[^>]+>/g, '').slice(0, 200).trim()
                : '');
        const image = post.heroImage || '/phylogo.ico';
        const url = window.location.href;
        const title = `${post.title} | PhysioActivity`;

        const tags = [
            { property: 'og:title', content: title },
            { property: 'og:description', content: description },
            { property: 'og:type', content: 'article' },
            { property: 'og:url', content: url },
            { property: 'og:image', content: image },
            { property: 'og:article:published_time', content: post.publishedDate || '' },
            { property: 'og:article:author', content: post.authorName || '' },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: description },
            { name: 'twitter:image', content: image },
        ];

        const created = [];
        tags.forEach(({ property, name, content }) => {
            if (!content) return;
            const el = document.createElement('meta');
            if (property) el.setAttribute('property', property);
            if (name) el.setAttribute('name', name);
            el.setAttribute('content', content);
            el.setAttribute('data-pa-blog-meta', '1');
            document.head.appendChild(el);
            created.push(el);
        });

        return () => {
            created.forEach((el) => {
                if (el.parentNode) el.parentNode.removeChild(el);
            });
        };
    }, [post]);

    // Helper to get initials
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const handleSave = () => {
        if (!slug) return;
        try {
            const next = !saved;
            if (next) {
                window.localStorage.setItem(`pa-blog-saved-${slug}`, '1');
            } else {
                window.localStorage.removeItem(`pa-blog-saved-${slug}`);
            }
            setSaved(next);
        } catch (e) {
            // Ignore — saved state is purely cosmetic without storage
        }
    };

    const handleShare = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            // Fallback: legacy execCommand path
            try {
                const ta = document.createElement('textarea');
                ta.value = window.location.href;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (e2) {
                // Give up silently
            }
        }
    };

    const handlePrint = () => {
        if (post && post.pdfUrl) {
            window.open(post.pdfUrl, '_blank', 'noopener');
            return;
        }
        window.print();
    };

    // Loading state
    if (loading) {
        return (
            <div className="blog-post-page">
                <div className="blog-post-loading">
                    <div className="blog-post-skeleton-header">
                        <div className="skeleton-line skeleton-tag"></div>
                        <div className="skeleton-line skeleton-title"></div>
                        <div className="skeleton-line skeleton-title short"></div>
                        <div className="skeleton-line skeleton-subtitle"></div>
                        <div className="skeleton-author-row">
                            <div className="skeleton-avatar"></div>
                            <div className="skeleton-author-lines">
                                <div className="skeleton-line skeleton-name"></div>
                                <div className="skeleton-line skeleton-date"></div>
                            </div>
                        </div>
                    </div>
                    <div className="blog-post-skeleton-body">
                        <div className="skeleton-line skeleton-text"></div>
                        <div className="skeleton-line skeleton-text"></div>
                        <div className="skeleton-line skeleton-text short"></div>
                        <div className="skeleton-line skeleton-text"></div>
                        <div className="skeleton-line skeleton-text"></div>
                        <div className="skeleton-line skeleton-text short"></div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !post) {
        return (
            <div className="blog-post-page">
                <div className="blog-post-error">
                    <div className="blog-post-error-content">
                        <h2>Article not found</h2>
                        <p>The article you're looking for doesn't exist or may have been moved.</p>
                        <Link to="/blog" className="blog-post-back-btn">
                            &larr; Back to all articles
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-post-page">
            <div className="blog-post-container">
                {/* Back Link */}
                <Link to="/blog" className="blog-post-back-link blog-post-no-print">
                    &larr; Back to all articles
                </Link>

                {/* Article Header */}
                <header className="blog-post-header">
                    <div className="blog-post-meta-top">
                        {post.category && (
                            <span className="blog-post-category">{post.category}</span>
                        )}
                        {post.readTime && (
                            <span className="blog-post-read-time">{post.readTime} read</span>
                        )}
                    </div>

                    <h1 className="blog-post-title">{post.title}</h1>

                    {post.subtitle && (
                        <p className="blog-post-subtitle">{post.subtitle}</p>
                    )}

                    {/* Author Section */}
                    <div className="blog-post-author-section">
                        <div className="blog-post-author-row">
                            <div className="blog-post-author-avatar">
                                {getInitials(post.authorName)}
                            </div>
                            <div className="blog-post-author-details">
                                <span className="blog-post-author-name">{post.authorName}</span>
                                {post.authorCredentials && (
                                    <span className="blog-post-author-credentials">{post.authorCredentials}</span>
                                )}
                                <span className="blog-post-date">{formatDate(post.publishedDate)}</span>
                            </div>
                        </div>
                        {post.peerReviewed && (
                            <div className="blog-post-peer-reviewed">
                                <span className="peer-reviewed-icon">&#10003;</span>
                                Peer Reviewed
                            </div>
                        )}
                    </div>
                </header>

                {/* Action Bar — Save / Share / Print */}
                <div className="blog-post-actions blog-post-no-print" role="toolbar" aria-label="Article actions">
                    <button
                        type="button"
                        className={`blog-post-action-btn${saved ? ' is-active' : ''}`}
                        onClick={handleSave}
                        aria-pressed={saved}
                        aria-label={saved ? 'Remove from saved' : 'Save for later'}
                    >
                        <span className="blog-post-action-icon" aria-hidden="true">
                            {saved ? '✓' : '☆'}
                        </span>
                        <span className="blog-post-action-label">
                            {saved ? 'Saved' : 'Save for later'}
                        </span>
                    </button>

                    <button
                        type="button"
                        className={`blog-post-action-btn${copied ? ' is-active' : ''}`}
                        onClick={handleShare}
                        aria-label="Copy link to article"
                    >
                        <span className="blog-post-action-icon" aria-hidden="true">
                            {copied ? '✓' : '↪'}
                        </span>
                        <span className="blog-post-action-label">
                            {copied ? 'Copied' : 'Copy link'}
                        </span>
                    </button>

                    <button
                        type="button"
                        className="blog-post-action-btn"
                        onClick={handlePrint}
                        aria-label={post.pdfUrl ? 'Open PDF' : 'Print or save as PDF'}
                    >
                        <span className="blog-post-action-icon" aria-hidden="true">&#8634;</span>
                        <span className="blog-post-action-label">
                            {post.pdfUrl ? 'Open PDF' : 'Print / PDF'}
                        </span>
                    </button>
                </div>

                {/* Key Takeaway Pull-Quote */}
                {post.keyTakeaway && (
                    <aside className="blog-post-takeaway" aria-label="Key takeaway">
                        <span className="blog-post-takeaway-label">Key takeaway</span>
                        <p className="blog-post-takeaway-text">{post.keyTakeaway}</p>
                    </aside>
                )}

                {/* Hero Image */}
                {post.heroImage && (
                    <div className="blog-post-hero-image">
                        <img src={post.heroImage} alt={post.heroImageAlt || post.title} />
                    </div>
                )}

                {/* Article Body */}
                <article
                    className="blog-post-body"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                    <div className="blog-post-tags">
                        {post.tags.map((tag, index) => (
                            <span key={index} className="blog-post-tag">{tag}</span>
                        ))}
                    </div>
                )}

                {/* Author Bio Card */}
                <div className="blog-post-author-card">
                    <div className="blog-post-author-card-avatar">
                        {getInitials(post.authorName)}
                    </div>
                    <div className="blog-post-author-card-info">
                        <span className="blog-post-author-card-label">Written by</span>
                        <span className="blog-post-author-card-name">{post.authorName}</span>
                        {post.authorCredentials && (
                            <span className="blog-post-author-card-credentials">{post.authorCredentials}</span>
                        )}
                        {post.authorBio && (
                            <p className="blog-post-author-card-bio">{post.authorBio}</p>
                        )}
                    </div>
                </div>

                {/* Related Posts Placeholder */}
                {/* TODO: Populate related posts from API based on post.category or post.tags */}
                <div className="blog-post-related blog-post-no-print">
                    <h2 className="blog-post-related-title">Related Articles</h2>
                    <p className="blog-post-related-placeholder">
                        More articles coming soon.
                    </p>
                </div>

                {/* Bottom Back Link */}
                <div className="blog-post-bottom-nav blog-post-no-print">
                    <Link to="/blog" className="blog-post-back-link">
                        &larr; Back to all articles
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default BlogPostPage;
